import * as config from '../config';
import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import ModelLoader from '../ModelLoader';
import { Appliance } from '../domain';
import { Box3, BoxGeometry, Material, Mesh, MeshStandardMaterial, Object3D, PlaneGeometry } from 'three';
import mfac from '../MaterialFactory';
import { getGeometrySize } from '../geometry';

const
    ml = ModelLoader.getInstance(),
    hitMaterial = new MeshStandardMaterial({
        color: 0x80FFFF,
        opacity: 0.5,
    });


const prepModel = (model: Object3D) => {
    model.traverse(obj => {
        if(obj instanceof Mesh) {
            obj.material.envMap = mfac.textureCube;
            obj.scale.set(1, 1, 1);
            obj.castShadow = obj.receiveShadow = true;
        }
    });
}

export default class ApplianceView extends BaseView {

    declare model: Appliance;

    root: Object3D;
    hit: Mesh;
    box: Box3 = new Box3();

    check: Object3D;

    faucet?: Object3D;

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);
        this.init();
    }

    add(child: Object3D) {
        super.add(child);
        child.traverse(obj => {
            if(obj instanceof Mesh) {
                this.highlights.add(obj);
            }
        });
        return this;
    }

    protected make(): void {
        this.name = 'Appliance';
        this.userData.isAppliance = true;
        this.userData.isRelocatable = true;

        this.hit = new Mesh(new BoxGeometry(), hitMaterial);
        this.hit.geometry.translate(0.5, 0.5, 0.5);
        //(this.hit.material as MeshStandardMaterial).transparent = true;
        this.hit.visible = false;
        this.hit.userData.isSelectable = true;
        this.hit.userData.isRelocatable = true;
        this.add(this.hit);
    }

    protected bind(): void {
        const
            updateAppearance = this.updateAppearance.bind(this),
            updateScale = this.updateScale.bind(this);

        this.model.bind('type', updateAppearance);
        this.model.on('make', updateAppearance);
        this.model.on('model', updateAppearance);

        this.model.on('width', updateScale);
        this.model.on('height', updateScale);
        this.model.on('depth', updateScale);

        this.model.bind('userChoice', async () => {
            if(this.model.userChoice == 'location') {
                this.check = await ml.get(`${config.models}/checkmark.glb`);

                // default position is the upper right
                this.check.position.x = this.model.width - 4;
                this.check.position.y = this.model.height - 4;
                this.check.position.z = this.model.depth + 0.25;

                switch(this.model.type) {
                    case 'sink':
                        this.check.position.x = this.model.width / 2;
                        this.check.position.y = 3;
                        this.check.position.z = this.model.depth;
                        break;
                    case 'farmhouse-sink':
                        this.check.position.x = this.model.width / 2;
                        break;

                }

                this.add(this.check);
            } else {
                this.check?.removeFromParent();
                delete this.check;
            }
        });
    }

    updateScale() {
        const { width, height, depth } = this.model;

        // TODO: need to use a box instead
        this.hit.scale.set(width, height, depth);
        this.hit.position.set(0, 0, 0);

        if(this.root instanceof Mesh && this.root.geometry instanceof BoxGeometry) {
            this.root.scale.set(1, 1, 1);
            this.root.rotation.set(0, 0, 0);
        } else {
            getGeometrySize(this.root, this.box);

            const
                w = this.box.max.x - this.box.min.x,
                h = this.box.max.y - this.box.min.y,
                d = this.box.max.z - this.box.min.z,
                sx = width == null ? 1 : width/w,
                sy = height == null ? 1 : height/h,
                sz = depth == null ? 1 : depth/d;

            this.root.scale.set(1, 1, 1);
            this.root.rotation.set(0, 0, 0);
            // TODO: investigate why this is inconsistent
            // compensate for the x-offset of the appliance model, which
            // probably has its origin centered (possibly eyballed). Note also
            // that we're rotating this 180 degrees, so it may look funny that
            // we're using the max here instead of the min.
            this.root.position.x = this.box.max.x * sx;
            this.root.rotation.y = Math.PI;
            this.root.scale.set(sx, sy, sz);
        }
    }

    updateAppearance() {
        this.updates.queue(
            () => this.loadAsset(),
            (object: Object3D) => {
                this.highlights.clear();
                object.traverse(obj => {
                    if(obj instanceof Mesh) {
                        this.highlights.add(obj);
                    }
                });
                this.root?.removeFromParent();
                this.root = object;

                prepModel(this.root);
//                this.root.traverse(obj => {
//                    if(obj instanceof Mesh) {
//                        obj.material.envMap = mfac.textureCube;
//                        obj.scale.set(1, 1, 1);
//                        obj.castShadow = obj.receiveShadow = true;
//                    }
//                });

                this.add(this.root);

                this.updateScale();

                this.pipelineUpdate();
            });
    }

    /**
     * Loads and sets up materials for generic assets.
     */
    async loadAsset() {
        switch(this.model.type) {
            case 'cooktop':
                return await ml.get(`${config.models}/gas-tabletop_02.glb`);

            case 'dishwasher':
                return await ml.get(`${config.models}/dishwasher_03.glb`);

            case 'microwave':
                return await ml.get(`${config.models}/microwave_01.glb`);

            case 'oven':
                return await ml.get(`${config.models}/oven_02_lo.glb`);

            case 'farmhouse-sink':
                this.faucet = await ml.get(`${config.models}/faucet_05.glb`);
                this.add(this.faucet);
                prepModel(this.faucet);
                this.faucet.position.set(this.model.width/2, 10, -2.5);
                this.faucet.rotation.set(0, Math.PI, 0);
                return await ml.get(`${config.models}/sink_06.glb`);

            case 'sink':
                this.faucet = await ml.get(`${config.models}/faucet_05.glb`);
                this.add(this.faucet);
                prepModel(this.faucet);
                this.faucet.position.set(this.model.width/2, 0, -3.5);
                this.faucet.rotation.set(0, Math.PI, 0);
                return await ml.get(`${config.models}/sink_07.glb`);

            case 'refrigerator':
                return await ml.get(`${config.models}/refrigerator-full_04_lo.glb`);

            case 'range':
                return await ml.get(`${config.models}/gas-range-oven-combo_04_lo.glb`);

            default:
                // this is temporary ... we shouldn't see these in production
                // TODO: remove this branch
                const { width, height, depth } = this.model;
                const geo = new BoxGeometry(width, height, depth);
                geo.translate(width/2, height/2, depth/2);
                const mesh = new Mesh(
                    geo, new MeshStandardMaterial({color: 0x808080}));
                mesh.castShadow = mesh.receiveShadow = true;
                return mesh;
        }
    }
}

